// All user-facing copy for the site, grouped by the component that renders
// it. Keeping text here (instead of inline in JSX) means the wording can be
// reviewed or edited in one place without touching component logic.

export const HEADER_STRINGS = {
  brandAlt: "The Burma Idiyappam",
  brandAriaLabel: "The Burma Idiyappam™ home",
  navAriaLabel: "Primary navigation",
  nav: {
    about: "About",
    story: "Our Story",
    subscription: "Subscription",
    menu: "Menu",
    contact: "Contact",
  },
  deliveryAriaLabel: "Delivery links",
  orderOnLabel: "Order on",
  swiggy: { label: "Swiggy", ariaLabel: "Order on Swiggy" },
  zomato: { label: "Zomato", ariaLabel: "Order on Zomato" },
  whatsapp: { label: "WhatsApp", ariaLabel: "WhatsApp Enquiry" },
};

export const HERO_STRINGS = {
  badge: "100% Steamed, Never Fried",
  titleBeforeScript: "Amma's idiyappam craft,",
  titleScript: "steamed fresh",
  titleAfterScript: "every single day.",
  copy: "The Burma Idiyappam™ serves soft steamed idiyappam, puttu, kozhukattai and homestyle South Indian snacks — made the traditional way, with simple ingredients, no deep-frying and no shortcuts.",
};

export const ABOUT_STRINGS = {
  eyebrow: "About Us",
  heading: "Traditional steaming. Honest ingredients. Zero shortcuts.",
  paragraphs: [
    "The Burma Idiyappam™ makes light, steamed food the way it's meant to be made: soft idiyappam, millet varieties, puttu, kozhukattai and simple traditional snacks, prepared fresh with the same care as a home kitchen — just built to a scale that works for everyday customers and bigger orders alike.",
    "Rooted in Madurai, we serve individual customers and also support larger-volume needs for hotels, events, hostels, institutions and recurring food supply partnerships.",
  ],
  highlightsAriaLabel: "Supply highlights",
  highlights: [
    "Hotels and cloud kitchens",
    "Hostels and institutions",
    "Events and family functions",
    "Employee snack subscriptions for offices",
    "Regular bulk supply plans",
  ],
};

export const STORY_STRINGS = {
  eyebrow: "Our Story",
  heading: "Second-generation craft, built for a bigger future.",
  paragraphs: [
    "This brand is my way of carrying forward my mother's idiyappam craft with a new scale and structure. After leaving my IT job, I chose to build a food business around the flavours I grew up with and the discipline needed to make them repeatable, every single day.",
    "As a woman entrepreneur, my ambition is to turn The Burma Idiyappam™ into a 100-outlet QSR brand while keeping the core promise intact: fresh steam, soft texture, honest ingredients and food that still feels close to home.",
  ],
  highlightsAriaLabel: "Founder story highlights",
  highlights: [
    "Second-generation food entrepreneur",
    "A woman-led QSR brand from Madurai",
    "Former IT professional turned founder",
    "Building towards 100 outlets",
  ],
};

export const SUBSCRIPTION_STRINGS = {
  eyebrow: "For Offices & Teams",
  heading: "A healthy, steamed snack subscription for your employees",
  paragraphBeforeStrong:
    "Swap fried, oily canteen snacks for a rotating menu of steamed idiyappam, puttu, kozhukattai, rotis and sundal, delivered to your office on a schedule that suits your team size.",
  paragraphStrong: "Structured subscription plans are launching soon",
  paragraphAfterStrong:
    "— for now, message us and we'll set up an early quote for your office.",
  highlightsAriaLabel: "Subscription highlights",
  highlights: [
    "Steamed only, never deep-fried",
    "Rotating weekly menu, no repeats",
    "Plans sized to your headcount",
    "Hygienic packaging, delivered on schedule",
  ],
  ctaParagraph:
    "Pricing depends on headcount, frequency and menu mix. Message us on WhatsApp or use the enquiry form below and we'll put together a custom quote for your office.",
  ctaPrimaryLabel: "Get A Subscription Quote",
  ctaSecondaryLabel: "Fill Enquiry Form",
};

export const MENU_STRINGS = {
  eyebrow: "Menu",
  heading: "Steamed favourites",
  paragraph:
    "Our full menu, grouped by category. Current prices, combos and availability are listed on delivery platforms.",
  tabsAriaLabel: "Menu categories",
  placeholderAlt: "Menu item placeholder",
};

export const CONTACT_STRINGS = {
  enquiryPanel: {
    eyebrow: "Enquiries",
    heading: "Corporate orders, subscriptions & bulk enquiries",
    paragraph:
      "Fill out the enquiry form or message us directly on WhatsApp — we typically reply within a few hours.",
    whatsappCta: "Message Us On WhatsApp",
  },
  eyebrow: "Visit Or Contact",
  heading: "The Burma Idiyappam™",
  labels: {
    phone: "Phone",
    instagram: "Instagram",
    location: "Location",
    hours: "Opening Hours",
  },
  instagramHandle: "@burmaidiyappam",
  hours: "3 PM to 11 PM",
  bniMemberText: "Member, Chanakyaas",
  socialAriaLabel: "Social links",
  whatsappAriaLabel: "WhatsApp",
  instagramAriaLabel: "Instagram",
  mapTitle: "The Burma Idiyappam™ location in Mahaboopalayam, Madurai",
};

export const FORM_STRINGS = {
  nameLabel: "Name",
  phoneLabel: "Phone",
  emailLabel: "Email (optional)",
  enquiryTypeLabel: "Enquiry Type",
  messageLabel: "Message",
  submitLabel: "Send Enquiry",
  submittingLabel: "Sending…",
  errors: {
    name: "Please enter a name using letters and spaces only.",
    phone: "Please enter a valid 10-digit mobile number.",
    email: "Please enter a valid email address, or leave it blank.",
    message: (max) => `Message must be ${max} characters or fewer.`,
  },
  success: "Thanks! Your enquiry has been sent — we'll get back to you soon.",
  genericError:
    "Something went wrong sending this. Please try WhatsApp instead.",
  notConfigured:
    "This form isn't connected yet — please message us on WhatsApp instead and we'll get right back to you.",
  enquiryTypes: [
    "General Enquiry",
    "Bulk / Corporate Order",
    "Employee Snack Subscription",
    "Event Catering",
    "Feedback",
  ],
};

export const FOOTER_STRINGS = {
  brand: "The Burma Idiyappam™",
};
